import httpService from "../../services/httpService";
import { ApiEndpoints } from "../../constants/apiEndpoints";

export type ServiceHealthState = "healthy" | "degraded" | "down" | "unknown";

export interface HealthServiceInfo {
  status: ServiceHealthState;
  raw?: unknown;
}

export interface HealthResponse {
  overall: ServiceHealthState;
  api: HealthServiceInfo;
  mongodb: HealthServiceInfo;
  firebase: HealthServiceInfo;
  checkedAt: string;
  latencyMs: number;
  raw: unknown;
}

function toState(value: unknown): ServiceHealthState {
  if (typeof value !== "string") return "unknown";

  const normalized = value.toLowerCase();
  if (["ok", "healthy", "up", "connected"].includes(normalized)) return "healthy";
  if (["degraded", "warning"].includes(normalized)) return "degraded";
  if (["down", "unhealthy", "error", "disconnected"].includes(normalized)) return "down";
  return "unknown";
}

function pickService(payload: any, keys: string[]): HealthServiceInfo {
  for (const key of keys) {
    const value = payload?.[key];

    if (typeof value === "string") {
      return { status: toState(value), raw: value };
    }

    if (value && typeof value === "object") {
      const statusValue = (value as any).status ?? (value as any).state;
      return { status: toState(statusValue), raw: value };
    }
  }

  return { status: "unknown" };
}

// Health check function - no auth required
export const checkHealth = async (): Promise<HealthResponse> => {
  const startedAt = performance.now();
  const response = await httpService.getRaw(ApiEndpoints.HEALTH);
  const payload = response.data;
  const healthData = payload?.data ?? payload;
  const services = healthData?.services;

  const api = pickService(services, ["api"]);
  const mongodb = pickService(services, ["mongodb"]);
  const firebase = pickService(services, ["firebase"]);
  const overall = toState(healthData?.status);

  return {
    overall,
    api,
    mongodb,
    firebase,
    checkedAt: new Date().toISOString(),
    latencyMs: Math.max(0, Math.round(performance.now() - startedAt)),
    raw: payload,
  };
};
